'use client';
import React, { useRef, useEffect, useCallback, useState } from 'react';

interface GradientColorPickerProps {
  value: string;
  onChange: (hex: string) => void;
}

function hsvToRgb(h: number, s: number, v: number): [number, number, number] {
  const f = (n: number) => {
    const k = (n + h / 60) % 6;
    return v - v * s * Math.max(0, Math.min(k, 4 - k, 1));
  };
  return [Math.round(f(5) * 255), Math.round(f(3) * 255), Math.round(f(1) * 255)];
}

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

function hexToHsv(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h = Math.round(h * 60);
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : d / max;
  return [h, s, max];
}

export default function GradientColorPicker({ value, onChange }: GradientColorPickerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hueRef = useRef<HTMLCanvasElement>(null);

  const [hsv, setHsv] = useState<[number, number, number]>(() => {
    try { return hexToHsv(value); } catch { return [0, 1, 1]; }
  });

  const [dotPos, setDotPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [huePos, setHuePos] = useState<number>(0);
  const [draggingCanvas, setDraggingCanvas] = useState(false);
  const [draggingHue, setDraggingHue] = useState(false);
  const [hexInput, setHexInput] = useState(value);

  const CANVAS_W = 220;
  const CANVAS_H = 160;
  const HUE_W = 18;
  const HUE_H = CANVAS_H;

  // Draw the gradient canvas
  const drawCanvas = useCallback((hue: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // White → hue color (horizontal)
    const hueColor = `hsl(${hue}, 100%, 50%)`;
    const gradH = ctx.createLinearGradient(0, 0, CANVAS_W, 0);
    gradH.addColorStop(0, '#ffffff');
    gradH.addColorStop(1, hueColor);
    ctx.fillStyle = gradH;
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // Transparent → black (vertical)
    const gradV = ctx.createLinearGradient(0, 0, 0, CANVAS_H);
    gradV.addColorStop(0, 'rgba(0,0,0,0)');
    gradV.addColorStop(1, 'rgba(0,0,0,1)');
    ctx.fillStyle = gradV;
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  }, []);

  // Draw the hue slider
  const drawHue = useCallback(() => {
    const canvas = hueRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const grad = ctx.createLinearGradient(0, 0, 0, HUE_H);
    const stops = [0, 60, 120, 180, 240, 300, 360];
    stops.forEach((deg) => grad.addColorStop(deg / 360, `hsl(${deg},100%,50%)`));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, HUE_W, HUE_H);
  }, []);

  // Sync dot position from hsv
  useEffect(() => {
    const [h, s, v] = hsv;
    setDotPos({ x: s * CANVAS_W, y: (1 - v) * CANVAS_H });
    setHuePos((h / 360) * HUE_H);
    drawCanvas(h);
    drawHue();
  }, []);

  // Redraw canvas when hue changes
  useEffect(() => {
    drawCanvas(hsv[0]);
  }, [hsv[0], drawCanvas]);

  useEffect(() => {
    drawHue();
  }, [drawHue]);

  const emitColor = useCallback((h: number, s: number, v: number) => {
    const [r, g, b] = hsvToRgb(h, s, v);
    const hex = rgbToHex(r, g, b);
    setHexInput(hex);
    onChange(hex);
  }, [onChange]);

  const handleCanvasInteract = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, CANVAS_W));
    const y = Math.max(0, Math.min(clientY - rect.top, CANVAS_H));
    setDotPos({ x, y });
    const s = x / CANVAS_W;
    const v = 1 - y / CANVAS_H;
    const newHsv: [number, number, number] = [hsv[0], s, v];
    setHsv(newHsv);
    emitColor(newHsv[0], newHsv[1], newHsv[2]);
  }, [hsv, emitColor]);

  const handleHueInteract = useCallback((clientY: number) => {
    const canvas = hueRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const y = Math.max(0, Math.min(clientY - rect.top, HUE_H));
    setHuePos(y);
    let h = Math.round((y / HUE_H) * 360);
    const newHsv: [number, number, number] = [h, hsv[1], hsv[2]];
    setHsv(newHsv);
    drawCanvas(h);
    emitColor(h, newHsv[1], newHsv[2]);
  }, [hsv, drawCanvas, emitColor]);

  // Mouse events
  const onCanvasMouseDown = (e: React.MouseEvent) => {
    setDraggingCanvas(true);
    handleCanvasInteract(e.clientX, e.clientY);
  };
  const onHueMouseDown = (e: React.MouseEvent) => {
    setDraggingHue(true);
    handleHueInteract(e.clientY);
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (draggingCanvas) handleCanvasInteract(e.clientX, e.clientY);
      if (draggingHue) handleHueInteract(e.clientY);
    };
    const onUp = () => { setDraggingCanvas(false); setDraggingHue(false); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [draggingCanvas, draggingHue, handleCanvasInteract, handleHueInteract]);

  // Touch events
  const onCanvasTouchStart = (e: React.TouchEvent) => {
    setDraggingCanvas(true);
    const t = e.touches[0];
    handleCanvasInteract(t.clientX, t.clientY);
  };
  const onHueTouchStart = (e: React.TouchEvent) => {
    setDraggingHue(true);
    const t = e.touches[0];
    handleHueInteract(t.clientY);
  };

  useEffect(() => {
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (draggingCanvas) handleCanvasInteract(t.clientX, t.clientY);
      if (draggingHue) handleHueInteract(t.clientY);
    };
    const onTouchEnd = () => { setDraggingCanvas(false); setDraggingHue(false); };
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    return () => { window.removeEventListener('touchmove', onTouchMove); window.removeEventListener('touchend', onTouchEnd); };
  }, [draggingCanvas, draggingHue, handleCanvasInteract, handleHueInteract]);

  const handleHexInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setHexInput(v);
    if (/^#[0-9a-fA-F]{6}$/.test(v)) {
      const newHsv = hexToHsv(v);
      setHsv(newHsv);
      setDotPos({ x: newHsv[1] * CANVAS_W, y: (1 - newHsv[2]) * CANVAS_H });
      setHuePos((newHsv[0] / 360) * HUE_H);
      drawCanvas(newHsv[0]);
      onChange(v);
    }
  };

  const [r, g, b] = hsvToRgb(hsv[0], hsv[1], hsv[2]);
  const currentHex = rgbToHex(r, g, b);

  return (
    <div className="inline-flex flex-col gap-3 select-none">
      {/* Picker area */}
      <div className="flex gap-2.5 items-start">
        {/* Gradient canvas */}
        <div className="relative rounded-lg overflow-hidden shadow-lg border border-zinc-700/60" style={{ width: CANVAS_W, height: CANVAS_H, cursor: 'crosshair' }}
          onMouseDown={onCanvasMouseDown}
          onTouchStart={onCanvasTouchStart}
        >
          <canvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} className="block" />
          {/* Draggable dot */}
          <div
            className="absolute pointer-events-none"
            style={{
              left: dotPos.x,
              top: dotPos.y,
              transform: 'translate(-50%, -50%)',
            }}
          >
            <div className="w-5 h-5 rounded-full border-2 border-white shadow-[0_0_0_1.5px_rgba(0,0,0,0.5),0_2px_8px_rgba(0,0,0,0.6)]"
              style={{ backgroundColor: currentHex }}
            />
          </div>
        </div>

        {/* Hue slider */}
        <div
          className="relative rounded-lg overflow-hidden shadow-lg border border-zinc-700/60 flex-shrink-0"
          style={{ width: HUE_W, height: HUE_H, cursor: 'ns-resize' }}
          onMouseDown={onHueMouseDown}
          onTouchStart={onHueTouchStart}
        >
          <canvas ref={hueRef} width={HUE_W} height={HUE_H} className="block" />
          {/* Hue indicator */}
          <div
            className="absolute left-0 right-0 pointer-events-none"
            style={{ top: huePos, transform: 'translateY(-50%)' }}
          >
            <div className="mx-auto w-4 h-4 rounded-full border-2 border-white shadow-[0_0_0_1.5px_rgba(0,0,0,0.5),0_2px_6px_rgba(0,0,0,0.6)]"
              style={{ backgroundColor: `hsl(${hsv[0]},100%,50%)` }}
            />
          </div>
        </div>
      </div>

      {/* Hex input + preview */}
      <div className="flex items-center gap-2.5">
        <div
          className="w-8 h-8 rounded-lg border border-zinc-600 flex-shrink-0 shadow-inner"
          style={{ backgroundColor: currentHex }}
        />
        <input
          type="text"
          value={hexInput}
          onChange={handleHexInput}
          maxLength={7}
          spellCheck={false}
          className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm font-mono text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all duration-150 uppercase"
          placeholder="#000000"
        />
      </div>
    </div>
  );
}
